import { Building2, Landmark, Briefcase } from "lucide-react";
import BentoGrid from "@/components/effects/BentoGrid";
import GradientText from "@/components/effects/GradientText";

interface ServiceCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  highlight?: boolean;
}

const ServiceCard = ({ icon, title, description, highlight }: ServiceCardProps) => {
  return (
    <div className="flex flex-col h-full">
      <div className={`w-14 h-14 rounded-lg flex items-center justify-center mb-6 transition-all duration-300 ${
        highlight 
          ? 'bg-gradient-to-br from-primary/20 to-accent/20' 
          : 'bg-primary/10 group-hover:bg-primary/15'
      }`}>
        <div className={highlight ? 'text-primary text-2xl' : 'text-primary'}>{icon}</div>
      </div>
      <h3 className={`font-semibold mb-3 font-display ${highlight ? 'text-2xl' : 'text-xl'} text-foreground`}>
        {highlight ? <GradientText>{title}</GradientText> : title}
      </h3>
      <p className="text-muted-foreground leading-relaxed flex-grow">{description}</p>
    </div>
  );
};

const ServiceCards = () => {
  const services = [
    {
      id: 'private-lending',
      icon: <Briefcase className="w-7 h-7" />,
      title: "Private Lending",
      description: "Quick financing solutions when traditional lenders say no. Ideal for short-term needs, renovations, or debt consolidation.",
      highlight: true,
    },
    {
      id: 'a-lending',
      icon: <Landmark className="w-7 h-7" />,
      title: "A-Lending",
      description: "Prime mortgage solutions for clients with excellent credit and stable income. Competitive rates from major banks and lenders.",
    },
    {
      id: 'b-lending',
      icon: <Building2 className="w-7 h-7" />,
      title: "B-Lending",
      description: "Alternative lending options for those with less-than-perfect credit or unique income situations. Flexible approval criteria.",
    },
  ];

  const bentoItems = services.map((service) => ({
    id: service.id,
    content: <ServiceCard {...service} />,
  }));

  return (
    <section className="py-20 px-4 bg-secondary/50">
      <div className="container mx-auto">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 font-display">
            Lending Solutions for Every Situation
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            We work with a wide network of lenders to find the perfect mortgage solution tailored to your unique needs.
          </p>
        </div>
        <div className="max-w-6xl mx-auto">
          <BentoGrid items={bentoItems} />
        </div>
      </div>
    </section>
  );
};

export default ServiceCards;
